import React, {useEffect, useState, useRef, useCallback} from 'react'
import {Modal} from 'react-bootstrap'
import {API_URL} from '../../../config/api.config'

interface StepState {
    status: 'pending' | 'in_progress' | 'completed' | 'failed'
    message: string
    startedAt: number | null
    completedAt: number | null
}

const STEP_TITLES = [
    'Preparing update',
    'Downloading latest version',
    'Verifying update',
    'Installing update',
    'Saving version info',
    'Restarting application',
]

const makeInitialSteps = (): StepState[] =>
    STEP_TITLES.map(() => ({status: 'pending', message: '', startedAt: null, completedAt: null}))

type Phase = 'updating' | 'restarting' | 'complete' | 'error'

interface Props {
    show: boolean
    onComplete: () => void
}

const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
}

export function UpdateProgressDialog({show, onComplete}: Props) {
    const [phase, setPhase] = useState<Phase>('updating')
    const [steps, setSteps] = useState<StepState[]>(makeInitialSteps())
    const [errorMessage, setErrorMessage] = useState('')
    const [newVersion, setNewVersion] = useState('')
    const [restartSeconds, setRestartSeconds] = useState(0)
    const [elapsedSeconds, setElapsedSeconds] = useState(0)
    const [downloadMb, setDownloadMb] = useState(0)
    const startTimeRef = useRef<number>(0)
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const esRef = useRef<EventSource | null>(null)

    const cleanup = useCallback(() => {
        if (esRef.current) {
            esRef.current.close()
            esRef.current = null
        }
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        if (elapsedRef.current) {
            clearInterval(elapsedRef.current)
            elapsedRef.current = null
        }
    }, [])

    const startHealthPolling = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current)
        if (timerRef.current) clearInterval(timerRef.current)

        setRestartSeconds(0)
        timerRef.current = setInterval(() => {
            setRestartSeconds((prev) => prev + 1)
        }, 1000)

        setTimeout(() => {
            pollRef.current = setInterval(async () => {
                try {
                    const controller = new AbortController()
                    const timeout = setTimeout(() => controller.abort(), 3000)
                    const response = await fetch(`${API_URL}/api/health`, {
                        signal: controller.signal,
                    })
                    clearTimeout(timeout)
                    if (response.ok) {
                        try {
                            const vc = new AbortController()
                            const vt = setTimeout(() => vc.abort(), 3000)
                            const versionResponse = await fetch(
                                `${API_URL}/api/open/version-check`,
                                {signal: vc.signal}
                            )
                            clearTimeout(vt)
                            if (versionResponse.ok) {
                                const data = await versionResponse.json()
                                setNewVersion(data.currentVersion || '')
                            }
                        } catch {
                            // version check failed but health is up
                        }
                        setPhase('complete')
                        if (pollRef.current) {
                            clearInterval(pollRef.current)
                            pollRef.current = null
                        }
                        if (timerRef.current) {
                            clearInterval(timerRef.current)
                            timerRef.current = null
                        }
                        if (elapsedRef.current) {
                            clearInterval(elapsedRef.current)
                            elapsedRef.current = null
                        }
                    }
                } catch {
                    // still restarting
                }
            }, 5000)
        }, 10000)
    }, [])

    useEffect(() => {
        if (!show) return

        setPhase('updating')
        setSteps(makeInitialSteps())
        setErrorMessage('')
        setNewVersion('')
        setRestartSeconds(0)
        setElapsedSeconds(0)
        setDownloadMb(0)
        startTimeRef.current = Date.now()

        // Elapsed timer
        elapsedRef.current = setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000))
        }, 1000)

        const es = new EventSource(`${API_URL}/api/open/self-update`)
        esRef.current = es

        es.addEventListener('step', (e: MessageEvent) => {
            const data = JSON.parse(e.data)
            const idx = data.step - 1
            const now = Date.now()
            setSteps((prev) => {
                const updated = [...prev]
                if (idx >= 0 && idx < updated.length) {
                    updated[idx] = {
                        ...updated[idx],
                        status: data.status,
                        message: data.message,
                        startedAt:
                            data.status === 'in_progress'
                                ? now
                                : updated[idx].startedAt,
                        completedAt:
                            data.status === 'completed' ? now : updated[idx].completedAt,
                    }
                }
                return updated
            })
            // Reset download progress when step 2 completes or a different step starts
            if (data.status === 'completed' && idx === 1) {
                setDownloadMb(0)
            }
        })

        es.addEventListener('download_progress', (e: MessageEvent) => {
            const mb = parseInt(e.data, 10)
            if (!isNaN(mb)) {
                setDownloadMb(mb)
            }
        })

        es.addEventListener('restarting', () => {
            es.close()
            esRef.current = null
            setPhase('restarting')
            startHealthPolling()
        })

        es.addEventListener('update_error', (e: MessageEvent) => {
            es.close()
            esRef.current = null
            setPhase('error')
            setErrorMessage(e.data)
        })

        es.onerror = () => {
            es.close()
            esRef.current = null
            setPhase((prev) => {
                if (prev === 'updating') {
                    startHealthPolling()
                    return 'restarting'
                }
                return prev
            })
        }

        return cleanup
    }, [show, cleanup, startHealthPolling])

    const handleReload = () => {
        window.location.reload()
    }

    const getStepDuration = (step: StepState): string | null => {
        if (step.startedAt && step.completedAt) {
            return formatDuration(step.completedAt - step.startedAt)
        }
        return null
    }

    const renderStepIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return (
                    <div className='symbol symbol-35px'>
                        <div className='symbol-label bg-light-success'>
                            <i className='ki-duotone ki-check fs-2 text-success'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                            </i>
                        </div>
                    </div>
                )
            case 'in_progress':
                return (
                    <div className='symbol symbol-35px'>
                        <div className='symbol-label bg-light-primary'>
                            <span className='spinner-border spinner-border-sm text-primary'></span>
                        </div>
                    </div>
                )
            case 'failed':
                return (
                    <div className='symbol symbol-35px'>
                        <div className='symbol-label bg-light-danger'>
                            <i className='ki-duotone ki-cross fs-2 text-danger'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                            </i>
                        </div>
                    </div>
                )
            default:
                return (
                    <div className='symbol symbol-35px'>
                        <div className='symbol-label bg-light'>
                            <i className='ki-duotone ki-time fs-2 text-gray-400'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                            </i>
                        </div>
                    </div>
                )
        }
    }

    if (!show) return null

    return (
        <Modal show={show} backdrop='static' keyboard={false} centered size='lg'>
            <Modal.Body className='p-10'>
                {phase === 'complete' ? (
                    <div className='text-center py-10'>
                        <div className='symbol symbol-80px mb-6'>
                            <div className='symbol-label bg-light-success'>
                                <i className='ki-duotone ki-check fs-3x text-success'>
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                </i>
                            </div>
                        </div>
                        <h2 className='fw-bold text-success mb-3'>Update Complete!</h2>
                        <p className='text-gray-600 fs-5 mb-2'>
                            Application has been updated successfully.
                        </p>
                        {newVersion && (
                            <p className='text-gray-500 fs-6 mb-2'>
                                Now running version{' '}
                                <strong className='text-dark'>{newVersion}</strong>
                            </p>
                        )}
                        <p className='text-gray-400 fs-7 mb-8'>
                            Total time: {formatDuration(elapsedSeconds * 1000)}
                        </p>
                        <button className='btn btn-primary btn-lg px-10' onClick={handleReload}>
                            <i className='ki-duotone ki-arrows-loop fs-3 me-2'>
                                <span className='path1'></span>
                                <span className='path2'></span>
                            </i>
                            Reload Application
                        </button>
                    </div>
                ) : phase === 'error' ? (
                    <div className='text-center py-10'>
                        <div className='symbol symbol-80px mb-6'>
                            <div className='symbol-label bg-light-danger'>
                                <i className='ki-duotone ki-cross fs-3x text-danger'>
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                </i>
                            </div>
                        </div>
                        <h2 className='fw-bold text-danger mb-3'>Update Failed</h2>
                        <p className='text-gray-600 fs-5 mb-8'>{errorMessage}</p>
                        <button className='btn btn-light-danger btn-lg px-10' onClick={onComplete}>
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <div className='text-center mb-8'>
                            <h2 className='fw-bold mb-2'>Updating Application</h2>
                            <p className='text-gray-500 fs-6 mb-0'>
                                Please do not close this window or turn off the device
                            </p>
                            <span className='text-gray-400 fs-7'>
                                Elapsed: {formatDuration(elapsedSeconds * 1000)}
                            </span>
                        </div>

                        <div className='d-flex flex-column gap-4 mb-8'>
                            {steps.map((step, index) => {
                                const duration = getStepDuration(step)
                                const isDownloading =
                                    index === 1 && step.status === 'in_progress'
                                return (
                                    <div key={index}>
                                        <div
                                            className={`d-flex align-items-center p-4 rounded-3 ${
                                                step.status === 'in_progress'
                                                    ? 'bg-light-primary border border-primary border-dashed'
                                                    : step.status === 'completed'
                                                    ? 'bg-light-success'
                                                    : step.status === 'failed'
                                                    ? 'bg-light-danger border border-danger border-dashed'
                                                    : 'bg-light'
                                            }`}
                                        >
                                            <div className='me-4'>
                                                {renderStepIcon(step.status)}
                                            </div>
                                            <div className='flex-grow-1'>
                                                <div
                                                    className={`fw-bold fs-6 ${
                                                        step.status === 'pending'
                                                            ? 'text-gray-400'
                                                            : 'text-dark'
                                                    }`}
                                                >
                                                    {STEP_TITLES[index]}
                                                </div>
                                                {step.message && step.status !== 'pending' && (
                                                    <div className='text-gray-500 fs-7 mt-1'>
                                                        {step.message}
                                                            </div>
                                                )}

                                                {/* Download progress bar */}
                                                {isDownloading && downloadMb > 0 && (
                                                    <div className='mt-2'>
                                                        <div className='d-flex align-items-center'>
                                                            <div
                                                                className='progress flex-grow-1'
                                                                style={{height: '8px'}}
                                                            >
                                                                <div
                                                                    className='progress-bar progress-bar-striped progress-bar-animated bg-primary'
                                                                    role='progressbar'
                                                                    style={{
                                                                        width: `${Math.min((downloadMb / 500) * 100, 100)}%`,
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <span className='text-primary fw-semibold fs-8 ms-3' style={{minWidth: '90px'}}>
                                                                {downloadMb} / 500 MB
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className='text-end ms-3' style={{minWidth: '55px'}}>
                                                {step.status === 'completed' && (
                                                    <>
                                                        <span className='badge badge-light-success fs-8 d-block'>
                                                            Done
                                                        </span>
                                                        {duration && (
                                                            <span className='text-gray-400 fs-9 mt-1 d-block'>
                                                                {duration}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {phase === 'restarting' && (
                            <div className='text-center p-6 bg-light-warning rounded-3 border border-warning border-dashed'>
                                <span className='spinner-border spinner-border-sm text-warning me-3'></span>
                                <span className='fw-bold text-warning fs-5'>
                                    Reconnecting to application...
                                </span>
                                <p className='text-gray-600 fs-7 mt-2 mb-0'>
                                    The application is restarting with the new version.
                                    <br />
                                    This can take up to 60 seconds on this device.
                                    <span className='text-gray-400 ms-2'>
                                        ({restartSeconds}s)
                                    </span>
                                </p>

                                {restartSeconds > 90 && (
                                    <div className='mt-4'>
                                        <p className='text-gray-600 fs-7 mb-3'>
                                            Taking longer than expected. The update was installed
                                            successfully. You can try reloading the page manually.
                                        </p>
                                        <button
                                            className='btn btn-sm btn-warning'
                                            onClick={handleReload}
                                        >
                                            Reload Page
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {phase === 'updating' && (
                            <div className='text-center'>
                                <div className='d-flex align-items-center justify-content-center text-gray-500 fs-7'>
                                    <i className='ki-duotone ki-information-3 fs-5 me-2 text-warning'>
                                        <span className='path1'></span>
                                        <span className='path2'></span>
                                        <span className='path3'></span>
                                    </i>
                                    Do not close this window or turn off the device during the
                                    update
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    )
}
